<template>
    <div class="relationship-container">
        <v-card class="floating-form">
            <v-card-title>
                <span class="headline">设置参数</span>
            </v-card-title>
            <v-card-text>
                <v-text-field v-model="limit" label="关系条数" type="number" />
                <v-text-field v-model="minWeight" label="人物阈值" type="number" />
                <v-text-field v-model="minRelationship" label="关系阈值" type="number" />
                <v-text-field v-model="focusedPerson" label="聚焦人物" type="text" />
            </v-card-text>
            <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn color="primary" @click="updateSettings">确认</v-btn>
                <v-btn @click="showForm = false">取消</v-btn>
            </v-card-actions>
        </v-card>
        <div v-if="loading" class="loading-overlay">
            <div class="loading-spinner"></div>
        </div>
        <div id="network" ref="networkContainer"></div>
    </div>
</template>

<script>
import { Network, DataSet } from 'vis-network/standalone';
import service from '@/utils/request';

export default {
    name: "RelationShip",
    data() {
        return {
            network: null,
            nodes: [],
            edges: [],
            loading: false,
            limit: 300, // 默认关系条数
            minWeight: 50, // 默认权重阈值
            focusedPerson: '', // 默认关心人物
            showForm: true, // 控制悬浮表单的显示，初始为true
            networkOptions: {
                nodes: {
                    shape: 'circularImage',
                    size: 120,
                    font: { 
                        size: 60,     // 显著增大人物姓名字号到60px
                        color: '#333',
                        face: 'Microsoft YaHei',
                        bold: 'bold',
                        vadjust: -10  // 增加标签与头像的距离
                    },
                    borderWidth: 4,
                    shadow: {
                        enabled: true,
                        color: 'rgba(0,0,0,0.2)',
                        size: 12
                    }
                },
                edges: {
                    width: 4,
                    smooth: false,
                    arrows: {
                        to: { enabled: true, scaleFactor: 0.8 }
                    },
                    font: {
                        size: 50,  // 显著增大关系文字字号到50px
                        face: 'Microsoft YaHei',
                        align: 'middle',
                        background: 'white',
                        strokeWidth: 10  // 增加文字背景描边宽度
                    },
                    color: {
                        inherit: false,
                        color: '#2b88d8'
                    }
                },
                physics: {
                    enabled: true,
                    forceAtlas2Based: {
                        gravitationalConstant: -2000,  // 进一步增大斥力
                        centralGravity: 0.001,         // 显著减小中心引力，让节点更自由
                        springLength: 600,             // 大幅增加弹簧长度
                        springConstant: 0.02,          // 减小弹簧系数，让连接更松弛
                        damping: 0.2,                  // 减小阻尼，让运动更自由
                        avoidOverlap: 2.0             // 增加重叠避免系数
                    },
                    solver: 'forceAtlas2Based',
                    stabilization: {
                        enabled: true,
                        iterations: 300,               // 增加迭代次数
                        updateInterval: 50,            // 增加更新间隔
                        fit: true
                    }
                },
                layout: {
                    improvedLayout: true,
                    randomSeed: 2
                },
                interaction: {
                    hover: true,
                    navigationButtons: true,
                    keyboard: true,
                    zoomView: true,
                    dragView: true,
                    multiselect: false,           // 禁用多选
                    selectable: true
                },
                // 配置视图范围
                configure: {
                    enabled: false
                }
            },
            personWeights: {}, // 存储每个人物的总权重
            minRelationship: 6, // 默认关系阈值
        }
    },
    methods: {
        async loadAllRelations() {
            try {
                this.loading = true;
                const response = await service.get('/wikiapi/relations/all', {
                    params: {
                        limit: this.limit,  // 使用用户设置的limit
                        minWeight: this.minWeight, // 使用用户设置的minWeight
                        minRelationship: this.minRelationship, // 添加关系阈值作为参数
                        focusedPerson: this.focusedPerson // 添加聚焦人物作为参数
                    }
                });
                if (response.data) {
                    this.nodes = response.data.nodes;
                    this.edges = response.data.edges;
                    this.initNetwork();
                    this.focusOnPerson(); // 聚焦到关心的人物
                }
            } catch (error) {
                console.error('加载关系数据失败:', error);
            } finally {
                this.loading = false;
            }
        },

        focusOnPerson() {
            const personNode = this.nodes.find(node => node.label === this.focusedPerson);
            if (personNode) {
                const positions = this.network.getPositions([personNode.id]);
                const position = positions[personNode.id];
                this.network.moveTo({
                    position: position,
                    scale: 0.2,
                    animation: {
                        duration: 1500,
                        easingFunction: 'easeInOutQuad'
                    }
                });
            }
        },

        updateSettings() {
            // 保存设置到 localStorage
            localStorage.setItem('relationshipSettings', JSON.stringify({
                limit: this.limit,
                minWeight: this.minWeight,
                focusedPerson: this.focusedPerson,
                minRelationship: this.minRelationship
            }));
            
            this.showForm = false; // 关闭表单
            this.loadAllRelations(); // 重新加载关系数据
        },

        loadSettings() {
            const savedSettings = localStorage.getItem('relationshipSettings');
            if (savedSettings) {
                const { limit, minWeight, focusedPerson, minRelationship } = JSON.parse(savedSettings);
                this.limit = limit;
                this.minWeight = minWeight;
                this.focusedPerson = focusedPerson;
                this.minRelationship = minRelationship;
            }
        },

        getRelationshipColor(relationship) {
            // 负面关系：红色
            const negativeRelations = [
                '敌人', '战争对手', '政敌', '叛将', '仇人',
                '谋害者', '被谋害者', '斩杀者', '被斩杀者',
                '背叛者', '被背叛者', '被俘虏者', '被杀者'
            ];

            if (negativeRelations.includes(relationship)) {
                return '#ff4d4f';  // 红色
            }
            return '#2b88d8';  // 默认蓝色
        },

        initNetwork() {
            const container = this.$refs.networkContainer;
            
            // 处理边的颜色和长度
            const edges = this.edges.map(edge => {
                const relationColor = edge.color || this.getRelationshipColor(edge.label); // 使用边的 color 字段，如果没有则使用默认关系颜色
                const weight = edge.weight || 1; // 假设边上有权重属性
                const length = Math.max(100, 600 - weight * 10); // 根据权重计算弹簧长度

                // 根据权重设置边的宽度，使用分档逻辑
                let edgeWidth;
                if (weight > 400) {
                    edgeWidth = 20; // 非常粗
                } else if (weight > 200) {
                    edgeWidth = 15; // 粗
                } else if (weight > 100) {
                    edgeWidth = 10; // 相当粗
                } else if (weight > 50) {
                    edgeWidth = 5; // 明显变粗
                } else if (weight > 25) {
                    edgeWidth = 3; // 轻微变粗
                } else if (weight > 10) {
                    edgeWidth = 2; // 默认宽度
                } else {
                    edgeWidth = 1; // 最细
                }

                return {
                    ...edge,
                    color: {
                        color: relationColor,
                        highlight: relationColor,
                        hover: relationColor
                    },
                    length: length, // 设置边的长度
                    width: edgeWidth, // 设置边的宽度
                    font: {
                        size: 50,
                        face: 'Microsoft YaHei',
                        align: 'middle',
                        background: 'white',
                        strokeWidth: 10,
                        color: relationColor  // 设置文字颜色与线条颜色一致
                    }
                };
            });

            // 处理节点的边框颜色和姓名颜色
            this.nodes.forEach(node => {
                const personId = node.id;
                const totalWeight = edges
                    .filter(edge => edge.from === personId || edge.to === personId)
                    .reduce((sum, edge) => sum + (edge.weight || 0), 0);
                
                // 根据总权重调整头像大小
                const avatarSize = Math.min(300, 100 + Math.sqrt(totalWeight)); // 将头像大小范围从100到300
                node.size = avatarSize; // 更新节点的大小

                // 动态调整姓名文本的大小
                const textSize = Math.max(40, avatarSize); // 根据头像大小设置文本大小，最小为40
                node.font = {
                    size: textSize, // 设置文本大小
                    face: 'Microsoft YaHei',
                    color: node.borderColor || '#000', // 使用边框颜色作为文本颜色
                    background: 'white',
                    strokeWidth: 10
                };

                // 使用 borderColor 字段设置边框颜色
                node.borderColor = node.borderColor || '#000'; // 默认边框颜色为黑色
                node.color = {
                    border: node.borderColor, // 设置边框颜色
                    background: '#fff' // 背景颜色
                };
                node.borderWidth = 4; // 设置边框宽度，增加边框的粗细
            });

            const data = {
                nodes: new DataSet(this.nodes),
                edges: new DataSet(edges)  // 使用处理后的边
            };
            
            this.network = new Network(container, data, {
                ...this.networkOptions,
                physics: {
                    enabled: true,
                    forceAtlas2Based: {
                        gravitationalConstant: -2000,
                        centralGravity: 0.001,
                        springLength: 150,
                        springConstant: 0.08,
                        damping: 0.4,
                        avoidOverlap: 1
                    },
                    solver: 'forceAtlas2Based',
                    stabilization: {
                        enabled: true,
                        iterations: 100,
                        updateInterval: 25
                    }
                }
            });
            
            // 延长稳定时间，分阶段进行
            this.network.once('stabilizationIterationsDone', () => {
                const freezeDuration = this.limit / 100 * 1000; // 计算定格之前的时间（毫秒）

                // 第一阶段：继续让物理引擎运行一段时间
                setTimeout(() => {
                    // 第二阶段：微调阶段，减小力度
                    this.network.setOptions({
                        physics: {
                            forceAtlas2Based: {
                                gravitationalConstant: -1000,
                                springConstant: 0.01
                            }
                        }
                    });
                    
                    // 第三阶段：最终定型并聚焦到【聚焦人物】
                    setTimeout(() => {
                        this.network.setOptions({ physics: { enabled: false } });
                        
                        // 找到聚焦人物节点
                        const focusedPersonNode = this.nodes.find(node => node.label === this.focusedPerson);
                        
                        if (focusedPersonNode) {
                            // 获取聚焦人物节点的位置
                            const positions = this.network.getPositions([focusedPersonNode.id]);
                            const position = positions[focusedPersonNode.id];
                            
                            // 直接移动到聚焦人物位置并放大
                            this.network.moveTo({
                                position: position,
                                scale: 0.2,
                                animation: {
                                    duration: 1500,
                                    easingFunction: 'easeInOutQuad'
                                },
                                offset: {
                                    x: 100,
                                    y: 0
                                }
                            });
                        } else {
                            // 如果没有找到刘备节点，则使用默认的自适应布局
                            this.network.fit({
                                animation: {
                                    duration: 1000,
                                    easingFunction: 'easeInOutQuad'
                                }
                            });
                        }
                    }, freezeDuration); // 使用计算的定格时间
                }, 2000);
            });

            // 修改双击事件：启动/禁用物理引擎
            this.network.on('doubleClick', () => {
                const physics = this.network.physics;
                this.network.setOptions({ physics: { enabled: !physics.physicsEnabled } }); // 切换物理引擎状态
            });

            // 添加鼠标悬浮事件
            this.network.on('hoverNode', (params) => {
                const nodeId = params.node;
                const node = this.network.body.nodes[nodeId];
                if (node) {
                    node.borderWidth = 6; // 悬浮时加粗边框
                    // 保持边框颜色不变
                    node.color.border = node.borderColor; // 确保边框颜色不变
                    this.network.redraw(); // 重新绘制网络
                }
            });

            this.network.on('blurNode', (params) => {
                const nodeId = params.node;
                const node = this.network.body.nodes[nodeId];
                if (node) {
                    node.borderWidth = 4; // 恢复边框宽度
                    this.network.redraw(); // 重新绘制网络
                }
            });
        }
    },
    mounted() {
        this.loadSettings(); // 加载设置
        this.loadAllRelations();
    },
    beforeDestroy() {
        if (this.network) {
            this.network.destroy();
        }
    }
};
</script>

<style scoped>
.relationship-container {
    width: 100%;
    height: calc(100vh - 120px);
    position: relative;
    background-color: #f8f9fa;
    padding: 20px;
}

.floating-form {
    position: absolute;
    top: 20px; /* 调整为你想要的位置 */
    left: 20px; /* 调整为你想要的位置 */
    z-index: 1000;
}

#network {
    width: 100%;
    height: 100%;
    border-radius: 8px;
    background-color: #fff;
    box-shadow: 0 2px 12px 0 rgba(0,0,0,0.1);
}

.loading-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(255,255,255,0.8);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1;
}

.loading-spinner {
    width: 40px;
    height: 40px;
    border: 4px solid #f3f3f3;
    border-top: 4px solid #3498db;
    border-radius: 50%;
    animation: spin 1s linear infinite;
}

@keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
}
</style>